import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { LeaderboardEntry } from '../api/types';
import { formatNumber, useJourneyLocale } from '../i18n/journey';
import { useUser } from '../state/UserContext';

export function Leaderboard() {
  const { me, updateMe } = useUser();
  const { t, lang } = useJourneyLocale();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .getLeaderboard(20)
      .then((res) => setEntries(res.entries))
      .catch((e) => setError(e instanceof Error ? e.message : t('leaderboard.loadFailed')))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleToggleOptIn = async () => {
    if (!me) return;
    setToggling(true);
    setError(null);
    try {
      await updateMe({ leaderboardOptIn: !me.leaderboardOptIn });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('leaderboard.toggleFailed'));
    } finally {
      setToggling(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">{t('leaderboard.title')}</h1>
      <p className="page-subtitle">{t('leaderboard.subtitle')}</p>

      <div className="leaderboard-layout">
        <div className="card leaderboard-optin">
          <div className="toggle-row">
            <div>
              <div className="toggle-row-label">{t('leaderboard.optInLabel')}</div>
              <div className="practice-item-meta">{t('leaderboard.optInHelp')}</div>
            </div>
            <button
              type="button"
              className={`switch${me?.leaderboardOptIn ? ' on' : ''}`}
              role="switch"
              aria-checked={Boolean(me?.leaderboardOptIn)}
              onClick={handleToggleOptIn}
              disabled={toggling || !me}
            >
              <span className="knob" />
            </button>
          </div>
        </div>

        <div className="leaderboard-results">
          {error && <p className="error-text">{error}</p>}

          {loading ? (
            <div className="center-page" style={{ minHeight: '30vh' }}>
              <div className="spinner" />
            </div>
          ) : entries.length === 0 ? (
            <div className="empty-state">{t('leaderboard.empty')}</div>
          ) : (
            <div className="leaderboard-list">
              {entries.map((e) => (
                <div
                  key={`${e.rank}-${e.displayName}`}
                  className={`leaderboard-row${e.displayName === me?.displayName ? ' self' : ''}`}
                >
                  <div className="leaderboard-rank">{formatNumber(lang, e.rank)}</div>
                  <div className="leaderboard-name">{e.displayName}</div>
                  <div className="leaderboard-streak">🔥 {formatNumber(lang, e.currentStreak)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
